import { validateParticipantsForMethod } from 'modules/calendar/utils/validateParticipantsForMethod';
import {
  positiveNumberSchema,
  nonNegativeNumberSchema
} from 'common/form/validationSchema/number';
import { usePatchRest } from 'common/hooks/usePatchRest';
import Yup from 'utils/validation';
import { ApiPaths } from '../constants/apiPaths';
import { availabilitySchema } from './apiSchemas';
import { AvailabilityMethod } from 'generated-graphql/graphql';

export const apiUpdateCalendarMeetingTemplateSchema = Yup.object({
  templateId: Yup.string().required(),
  title: Yup.string().min(1, 'Enter meeting title.').required(),
  description: Yup.string().optional(),
  videoConference: Yup.boolean().default(false).required(),
  durationMinutes: positiveNumberSchema
    .required()
    .max(1440, 'Duration must be at most 1440 minutes (24 hours)'),
  participants: Yup.array(
    Yup.object({
      email: Yup.string().email('Invalid email format').required(),
      name: Yup.string().min(1, 'Name is required').required(),
      isOrganizer: Yup.boolean().required(),
      autoCollectAvailability: Yup.boolean().optional()
    })
  )
    .required()
    .test('participants-for-method', function (value) {
      const error = validateParticipantsForMethod(
        this.parent.availabilityMethod,
        value
      );

      return error ? this.createError({ message: error }) : true;
    }),
  availability: availabilitySchema.required(),
  timezone: Yup.string()
    .test(
      'valid-timezone',
      'Must be valid IANA timezone identifier',
      (timeZone) => {
        try {
          Intl.DateTimeFormat(undefined, { timeZone });
          return true;
        } catch {
          return false;
        }
      }
    )
    .required(),
  sendMeetingReminder: positiveNumberSchema.required(),
  buffer: nonNegativeNumberSchema
    .max(120)
    .test(
      'buffer-multiple-of-5',
      'Buffer must be multiple of 5 minutes',
      function (value) {
        return (value ?? 0) % 5 === 0;
      }
    )
    .required(),
  // calendar-service accepts 0 (no notice required), and the form allows it.
  noticePeriodMinutes: nonNegativeNumberSchema.required(),
  availableDaysInTheFuture: positiveNumberSchema.required(),
  location: Yup.string().optional(),
  // The participants test above needs it to allow an organizer-less pool.
  availabilityMethod: Yup.mixed<AvailabilityMethod>()
    .oneOf(Object.values(AvailabilityMethod))
    .optional(),
  // Absent leaves the stored hours alone. .default(undefined) stops yup
  // materialising {} and failing its own inner required.
  availabilityOutreachSettings: Yup.object({
    requiredHours: positiveNumberSchema.min(1).max(168).required()
  })
    .default(undefined)
    .optional()
}).required();

export type APIUpdateCalendarSchema = Yup.InferType<
  typeof apiUpdateCalendarMeetingTemplateSchema
>;

export const useUpdateCalendarMeetingTemplate = () =>
  usePatchRest<{ data: { id: string } }, APIUpdateCalendarSchema>(
    ApiPaths.UPDATE_CALENDAR_MEETING_TEMPLATE
  );
